import { Box, Button, Checkbox, Divider, FormLabel, TextField, Typography } from '@mui/material';
import InstagramSerialStory from '../common/InstagramSerialStory';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useRef, useState } from 'react';
import { CampaignContext } from '../../contexts/CampaignContext';
import { MediaFile } from '../../interfaces/file';
import InstagramPost from '../common/InstagramPost';
import PreviewStaticTheme from '../common/PreviewStaticTheme';
import usePlatformCardColors from '../../hooks/usePlatformCardColors';
import { getMediaLogo } from '../../utils/file';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';

type PreviewInstagramPostProps = {
	showInstagramSkleton: boolean;
	showFinalForwardContent: boolean;
	contentInstructions: string;
	medias: MediaFile[];
	postCaption: string;
	forwardModalLink: string;
	displayTags: string;
	data?: { image: File; caption: string; type: string; dataType: string }[];
	showProfileChecked: number | null;
	handleOpenPostModal: () => void;
	handleResetData: () => void;
};

const PreviewInstagramPost: React.FC<PreviewInstagramPostProps> = ({ postCaption, data, showInstagramSkleton, showFinalForwardContent, contentInstructions, medias, handleOpenPostModal, handleResetData, forwardModalLink, displayTags, showProfileChecked }) => {
	const { t } = useTranslation();
	const swiperRef = useRef<any>(null);
	const { selectedContentType, selectedPlatform, IsSerialStory } = useContext(CampaignContext);
	const { borderColorClass } = usePlatformCardColors(selectedPlatform!);
	const isPost = selectedContentType === 'post';

	const [activeIndex, setActiveIndex] = useState<number>(0);
	const [imageSrc, setImageSrc] = useState();

	const handleSlideChange = (swiper: any) => {
		setActiveIndex(swiper.activeIndex);
	};

	useEffect(() => {
		if (!data || data.length === 0) {
			setImageSrc(undefined);
			return;
		}

		const lastImage: any = data[data.length - 1]?.image;
		const image = lastImage instanceof File ? URL.createObjectURL(lastImage) : typeof lastImage === 'string' ? '/images/campaigns/' + lastImage : (lastImage?.url ?? null);

		setImageSrc(image);
	}, [data]);
	return (
		<>
			{showInstagramSkleton ? (
				<>
					<Box className="relative pt-10">
						<Box className={`flex gap-3`}>
							{selectedPlatform === 'influenceral' && !isPost && IsSerialStory ? (
								<>
									<div className="flex flex-wrap gap-4">
										{data &&
											data.length > 0 &&
											data.map(item => (
												<Box className="w-80">
													<InstagramSerialStory file={item.image} dataType={item.dataType} caption={item.caption} type={item.type} />
												</Box>
											))}
										<Button variant="text" className="absolute left-5 top-0 m-0 flex self-end px-0" onClick={handleResetData}>
											{t('edit')}
										</Button>
										<Box className="flex h-96 w-80 cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-action-active bg-grey-100 px-10 text-sm text-text-secondary" onClick={handleOpenPostModal}>
											<p className="text-center">{isPost ? t('add_post_button_text') : t('add_story_create_button_text')}</p>
											<Button className="mt-6 w-full" size="large">
												{isPost ? t('add_post') : t('add_story_create')}
											</Button>
										</Box>
									</div>
								</>
							) : (
								<>
									<Box className="w-80">
										<Box className={`relative min-h-96 rounded-2xl border ${borderColorClass}`}>
											<Box className="flex items-center justify-between px-3 py-2">
												<Typography className="text-base font-medium text-grey-900">{selectedContentType ? t(selectedContentType) : t('post')}</Typography>
												<Divider className={`mx-2 grow border ${borderColorClass}`} />
												<img src={getMediaLogo(selectedPlatform!)} alt={`${t(selectedPlatform!)} logo`} className="mb-1 h-8 w-8" />
											</Box>
											<Divider className={`border-1 ${borderColorClass}`} />
											<div className="relative">
												{medias.length > 0 && (
													<div className="absolute top-3 z-20 flex w-full justify-center gap-2 px-3">
														{Array(medias.length)
															.fill(2)
															.map((item, index) => (
																<span key={item} className={`h-1 w-full rounded-sm ${activeIndex === index ? 'bg-white' : 'bg-gray-400'}`}></span>
															))}
													</div>
												)}
												{data ? (
													<div className="mx-auto w-full">
														{selectedPlatform === 'instagram' && !isPost ? (
															<img src={imageSrc} alt="Instagram file preview" className="h-96 w-full object-cover" />
														) : medias.length > 0 ? (
															<Swiper ref={swiperRef} slidesPerView={1} pagination={true} modules={[Pagination]} className="mySwiper" onSlideChange={handleSlideChange}>
																{medias.slice(0, 10).map((media, index) => (
																	<SwiperSlide key={index}>
																		<div className={`relative`}>{media.type === 'image' ? <img src={URL.createObjectURL(media.file)} alt={`Post ${index + 1}`} className={`h-96 w-full object-cover`} /> : <video src={URL.createObjectURL(media.file)} className="h-64 w-full object-cover" controls />}</div>
																	</SwiperSlide>
																))}
															</Swiper>
														) : (
															<div className="flex h-full items-center justify-center text-text-secondary">{t('no_image_selected')}</div>
														)}
													</div>
												) : (
													<div className="flex h-full items-center justify-center text-text-secondary">{t('no_image_selected')}</div>
												)}
											</div>
										</Box>
									</Box>
									<Button variant="text" className="absolute left-5 top-0 m-0 flex self-end px-0" onClick={handleResetData}>
										{t('edit')}
									</Button>
									<Box className="w-9/12">
										<TextField
											InputProps={{
												readOnly: true,
											}}
											fullWidth
											label={t('instructions')}
											multiline
											rows={2}
											value={contentInstructions}
										/>
										<TextField
											InputProps={{
												readOnly: true,
											}}
											className="mt-5"
											fullWidth
											label={t('caption')}
											multiline
											rows={2}
											value={postCaption}
										/>
									</Box>
								</>
							)}
						</Box>
					</Box>
				</>
			) : showFinalForwardContent ? (
				<Box className="flex w-full gap-6">
					{!isPost ? (
						<Box className="w-96">
							<PreviewStaticTheme data={data} />
						</Box>
					) : (
						<Box className="w-80">
							<InstagramPost medias={medias} contentReady={false} staticContent={true} caption={postCaption} />
						</Box>
					)}
					<div className="flex w-full flex-col gap-4">
						<Button variant="text" className="m-0 flex self-end px-0" onClick={handleResetData}>
							{t('edit')}
						</Button>
						<Box className="flex w-full gap-6">
							<TextField className="grow basis-0" fullWidth label={isPost ? t('your_forward_link') : t('link')} value={forwardModalLink} InputProps={{ readOnly: true }} />
							{!isPost && <TextField className="grow basis-0" fullWidth label={t('tag')} value={displayTags} InputProps={{ readOnly: true }} />}
						</Box>
						<TextField fullWidth label={t('instructions')} multiline rows={10} value={contentInstructions} InputProps={{ readOnly: true }} />
						{!isPost && (
							<Box className="me-16 flex items-center rounded-lg">
								<Checkbox readOnly size="large" className="m-0" checked={Boolean(showProfileChecked)} />
								<FormLabel className="text-base text-text-primary">{t('show_profile')}</FormLabel>
							</Box>
						)}
					</div>
				</Box>
			) : (
				<Box className="flex h-96 w-80 cursor-pointer flex-col items-center justify-center rounded-3xl border border-dashed border-action-active bg-grey-100 px-10 text-sm text-text-secondary" onClick={() => handleOpenPostModal()}>
					<p className="text-center">{isPost ? t('add_post_button_text') : t('add_story_create_button_text')}</p>
					<Button className="mt-6 w-full" size="large">
						{isPost ? t('add_post') : t('add_story_create')}
					</Button>
				</Box>
			)}
		</>
	);
};

export default PreviewInstagramPost;
